import React, { useState, useEffect, useCallback } from "react";
import { Form, Card, Button, Row, Col, Modal } from "react-bootstrap";
import { useGlobalState } from "../../../../services/hostelConst";
import Select from "react-select";
import { debounce } from "lodash";

import {
  callToast,
  showErrorMessages,
} from "../../../../services/utilityFunctions";
import {
  addLibrary,
  updateLibrary,
} from "../libraryServices/libraryManagement";
import {
  getRegionsList,
  getSitesList,
} from "../../../../services/Organization-services";
import { getUserList } from "../../../../services/Users-services";

const AddLibraryForm = ({ navigate, refresh }) => {
  const { closeModal, modalState } = useGlobalState();
  const masterData = modalState.data;
  console.log("Library modal state data:", masterData);

  const [isLoading, setIsLoading] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [formData, setFormData] = useState({
    libraryName: "",
    libraryDescription: "",
    regionId: "",
    siteId: "",
    libraryHead: "",
    libraryId: null,
  });
  const [errors, setErrors] = useState({});
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "success",
  });

  const [regionOptions, setRegionOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (masterData) {
      console.log("Loading library data for editing:", masterData);
      setFormData({
        libraryName: masterData.NAME,
        libraryDescription: masterData.DESCRIPTION || "",
        regionId: masterData._REGION || "",
        siteId: masterData._SITE || "",
        libraryHead: masterData.LIBRARY_HEAD || masterData.libraryHead || "",
        libraryId: masterData._ID || masterData.id || null,
      });

      // Load related data if we're editing
      fetchRegions();
      if (masterData.REGION_ID || masterData.regionId) {
        fetchSites(masterData.REGION_ID || masterData.regionId);
      }
      if (
        (masterData.REGION_ID || masterData.regionId) &&
        (masterData.SITE_ID || masterData.siteId)
      ) {
        fetchUsers(
          masterData.REGION_ID || masterData.regionId,
          masterData.SITE_ID || masterData.siteId
        );
      }
    } else {
      // Load regions by default
      fetchRegions();
    }
  }, [masterData]);

  // Fetch regions
  const fetchRegions = async () => {
    try {
      setLoadingRegions(true);
      const response = await getRegionsList({ pageNum: 1, query: "" });
      if (response.status === "success") {
        const entityList =
          response.data.entityList || response.data.response?.entityList || [];
        const options = entityList.map((region) => ({
          value: region._ID,
          label: region.NAME,
        }));
        setRegionOptions(options);

        // If we're editing, select the current region
        if (masterData && (masterData.REGION_ID || masterData.regionId)) {
          const currentRegion = options.find(
            (option) =>
              option.value === (masterData.REGION_ID || masterData.regionId)
          );
          setSelectedRegion(currentRegion || null);
        }
      }
    } catch (error) {
      console.error("Error fetching regions:", error);
      const myButton = document.getElementById("myToastButton");
      callToast("error", "Error", "Failed to load regions", myButton);
    } finally {
      setLoadingRegions(false);
    }
  };

  // Fetch sites based on selected region
  const fetchSites = async (regionId) => {
    if (!regionId) return;

    try {
      setLoadingSites(true);
      const response = await getSitesList({
        pageNum: 1,
        query: "",
        regionId: regionId,
      });

      if (response.status === "success") {
        const entityList =
          response.data.entityList || response.data.response?.entityList || [];
        const options = entityList.map((site) => ({
          value: site._ID,
          label: site.NAME,
        }));
        setSiteOptions(options);

        // If we're editing, select the current site
        if (masterData && (masterData.SITE_ID || masterData.siteId)) {
          const currentSite = options.find(
            (option) =>
              option.value === (masterData.SITE_ID || masterData.siteId)
          );
          setSelectedSite(currentSite || null);
        }
      }
    } catch (error) {
      console.error("Error fetching sites:", error);
      const myButton = document.getElementById("myToastButton");
      callToast("error", "Error", "Failed to load sites", myButton);
    } finally {
      setLoadingSites(false);
    }
  };

  // Fetch users based on selected region and site
  const fetchUsers = async (regionId, siteId) => {
    if (!regionId || !siteId) return;

    try {
      setLoadingUsers(true);
      const response = await getUserList({
        pageNum: 1,
        query: "",
        regionId: regionId,
        siteId: siteId,
      });

      if (response.status === "success") {
        const entityList =
          response.data.entityList || response.data.response?.entityList || [];
        const options = entityList.map((user) => ({
          value: user._ID,
          label: `${user.FIRST_NAME || ""} ${user.LAST_NAME || ""} (${
            user.EMAIL || "No email"
          })`,
        }));
        setUserOptions(options);

        // If we're editing, select the current user
        if (masterData && (masterData.LIBRARY_HEAD || masterData.libraryHead)) {
          const currentUser = options.find(
            (option) =>
              option.value ===
              (masterData.LIBRARY_HEAD || masterData.libraryHead)
          );
          setSelectedUser(currentUser || null);
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      const myButton = document.getElementById("myToastButton");
      callToast("error", "Error", "Failed to load users", myButton);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle region selection
  const handleRegionChange = (selectedOption) => {
    setSelectedRegion(selectedOption);
    setSelectedSite(null);
    setSelectedUser(null);
    setSiteOptions([]);
    setUserOptions([]);

    setFormData((prev) => ({
      ...prev,
      regionId: selectedOption ? selectedOption.value : "",
      siteId: "",
      libraryHead: "",
    }));

    if (selectedOption) {
      fetchSites(selectedOption.value);
    }
  };

  // Handle site selection
  const handleSiteChange = (selectedOption) => {
    setSelectedSite(selectedOption);
    setSelectedUser(null);
    setUserOptions([]);

    setFormData((prev) => ({
      ...prev,
      siteId: selectedOption ? selectedOption.value : "",
      libraryHead: "",
    }));

    if (selectedOption && selectedRegion) {
      fetchUsers(selectedRegion.value, selectedOption.value);
    }
  };

  // Handle user selection
  const handleUserChange = (selectedOption) => {
    setSelectedUser(selectedOption);
    setFormData((prev) => ({
      ...prev,
      libraryHead: selectedOption ? selectedOption.value : "",
    }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.libraryName?.trim())
      newErrors.libraryName = "Library Name is required";
    if (!formData.libraryDescription?.trim())
      newErrors.libraryDescription = "Description is required";
    if (!formData.regionId) newErrors.regionId = "Region is required";
    if (!formData.siteId) newErrors.siteId = "Site is required";
    if (!formData.libraryHead)
      newErrors.libraryHead = "Library Head is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    handleConfirmedSubmit();
  };

  // Handle the confirmed submission
  const handleConfirmedSubmit = async () => {
    setIsLoading(true);

    try {
      const response = masterData
        ? await updateLibrary({
            ...formData,
            libraryId: formData.libraryId,
          })
        : await addLibrary(formData);

      if (response.status === "success") {
        const myButton = document.getElementById("myToastButton");
        callToast(
          "success",
          "Success",
          `Library ${masterData ? "updated" : "added"} successfully!`,
          myButton
        );

        // Call refresh if available
        if (typeof refresh === "function") {
          await refresh({ columnRefresh: false, isLoading: false });
        }

        // Close modal and/or navigate
        setTimeout(() => {
          closeModal();
          if (!masterData) {
            navigate("/organisation/library/library-list");
          }
        }, 2000);
      } else if (response.status === "fail") {
        showErrorMessages({ data: response, navigate });
        const myButton = document.getElementById("myToastButton");
        callToast(
          "error",
          "Error",
          response.message ||
            `Failed to ${masterData ? "update" : "add"} library`,
          myButton
        );
      }
    } catch (error) {
      showErrorMessages({
        data: {
          message: `Failed to ${masterData ? "update" : "add"} library`,
          errors: error.message,
        },
        navigate,
      });
      const myButton = document.getElementById("myToastButton");
      callToast("error", "Error", "An unexpected error occurred", myButton);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card.Body className="px-4 py-4">
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Library Name</Form.Label>
            <Form.Control
              type="text"
              name="libraryName"
              value={formData.libraryName}
              onChange={handleInputChange}
              placeholder="Enter library name"
              isInvalid={!!errors.libraryName}
              className="shadow-sm"
            />
            <Form.Control.Feedback type="invalid">
              {errors.libraryName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Description</Form.Label>
            <Form.Control
              as="textarea"
              name="libraryDescription"
              value={formData.libraryDescription}
              onChange={handleInputChange}
              placeholder="Enter library description"
              rows={3}
              isInvalid={!!errors.libraryDescription}
              className="shadow-sm"
            />
            <Form.Control.Feedback type="invalid">
              {errors.libraryDescription}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Region</Form.Label>
            <Select
              value={selectedRegion}
              onChange={handleRegionChange}
              options={regionOptions}
              isLoading={loadingRegions}
              placeholder="Select Region"
              isClearable
              className={errors.regionId ? "is-invalid" : ""}
            />
            {errors.regionId && (
              <div className="invalid-feedback d-block">{errors.regionId}</div>
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Site</Form.Label>
            <Select
              value={selectedSite}
              onChange={handleSiteChange}
              options={siteOptions}
              isLoading={loadingSites}
              placeholder={
                selectedRegion ? "Select Site" : "Please select a Region first"
              }
              isDisabled={!selectedRegion}
              isClearable
              className={errors.siteId ? "is-invalid" : ""}
            />
            {errors.siteId && (
              <div className="invalid-feedback d-block">{errors.siteId}</div>
            )}
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="fw-bold">Library Head</Form.Label>
            <Select
              value={selectedUser}
              onChange={handleUserChange}
              options={userOptions}
              isLoading={loadingUsers}
              placeholder={
                selectedSite
                  ? "Select Library Head"
                  : "Please select a Site first"
              }
              isDisabled={!selectedSite}
              isClearable
              className={errors.libraryHead ? "is-invalid" : ""}
            />
            {errors.libraryHead && (
              <div className="invalid-feedback d-block">
                {errors.libraryHead}
              </div>
            )}
          </Form.Group>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="light" onClick={closeModal} className="px-3">
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isLoading}
              className="px-4"
            >
              {isLoading
                ? masterData
                  ? "Updating..."
                  : "Adding..."
                : masterData
                ? "Update Library"
                : "Add Library"}
            </Button>
          </div>
        </Form>
      </Card.Body>
      <Modal
        show={showResponseModal}
        onHide={() => setShowResponseModal(false)}
        centered
        className={`response-modal ${modalConfig.type}`}
      >
        <Modal.Body className="text-center py-4">
          <div className={`response-icon ${modalConfig.type}`}>
            {modalConfig.type === "success" ? "✓" : "✕"}
          </div>
          <h5 className="mt-3">{modalConfig.title}</h5>
          <p className="mb-0">{modalConfig.message}</p>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddLibraryForm;
